import type React from 'react';

import { translationKey } from '@change-corgi/core/i18n';
import { Translate, TypedTranslate, useI18n } from '@change-corgi/core-react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Input } from '@change-corgi/design-system-legacy/components/forms';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { useTrackView } from 'src/app/shared/hooks/tracking';

import { useCreateVanityUrlForm } from './hooks/useCreateVanityUrlForm';

const Errors: React.FC<{ errors: string[] }> = ({ errors }) => {
	return (
		<>
			{errors.map((error) => (
				<p key={error}>{error}</p>
			))}
		</>
	);
};

const TIPS = [
	{ text: translationKey('corgi.petition.starter_dashboard.grow_petition.copy_link.vanity_link_tip_1') },
	{ text: translationKey('corgi.petition.starter_dashboard.grow_petition.copy_link.vanity_link_tip_2') },
	{ text: translationKey('corgi.petition.starter_dashboard.grow_petition.copy_link.vanity_link_tip_3') },
];

type Props = { petitionId: string; displayedSignatures: number };

export const CreateVanityUrlForm: React.FC<Props> = ({ petitionId, displayedSignatures }) => {
	const { translate } = useI18n();
	const {
		data: { loading, vanityUrl, urlError, wordCount },
		actions: { handleChange, handleSubmit },
	} = useCreateVanityUrlForm({ petitionId, displayedSignatures });

	useTrackView('custom_url_create_view', {
		petition_id: `petition_${petitionId}`,
		num_signatures: displayedSignatures,
	});

	return (
		<Box>
			<Text as="p" mt="16px" id="vanity-modal-description">
				<Translate value="corgi.petition.starter_dashboard.grow_petition.copy_link.vanity_link_description" />
			</Text>
			<form onSubmit={handleSubmit} data-qa="create-vanity-link-form" noValidate>
				<Flex sx={{ justifyContent: 'flex-end', mt: 24 }}>
					<Text size="caption" sx={{ px: '8px' }}>
						{wordCount}
					</Text>
				</Flex>
				<Flex sx={{ gap: '4px', alignItems: 'flex-start' }}>
					<Box sx={{ lineHeight: '40px', flexShrink: 0 }}>
						<Text size="caption">Change.org/</Text>
					</Box>
					<Box sx={{ flexGrow: 1 }}>
						<Input
							aria-labelledby="vanity-modal-title"
							aria-describedby="vanity-modal-description"
							id="vanity-url-input"
							name="vanity-url-input"
							placeholder={translate(
								'corgi.petition.starter_dashboard.grow_petition.copy_link.vanity_link_input_placeholder',
							)}
							type="text"
							onChange={handleChange}
							maxLength={100}
							minLength={10}
							error={urlError ? <Errors errors={urlError} /> : null}
							data-testid="customUrl-input-text"
							value={vanityUrl}
							required
						/>
					</Box>
				</Flex>

				<Box
					as="ul"
					variant="bordered"
					role="list"
					sx={{
						borderColor: 'neutral-grey100',
						backgroundColor: 'primary-greyBackground',
						p: 16,
						mt: 16,
						listStyleType: 'disc',
					}}
				>
					{TIPS.map((item, index: number) => {
						return (
							<Box as="li" role="listitem" key={`vanity-link-tips-${index + 1}`} sx={{ ml: '16px' }}>
								<Text as="p" size="small">
									<TypedTranslate value={item.text} />
								</Text>
							</Box>
						);
					})}
				</Box>
				<Button
					data-testid="customUrl-create-button"
					variant="primary"
					type="submit"
					loading={loading}
					sx={{ display: 'block', mt: '16px', ml: 'auto' }}
				>
					<Translate value="corgi.petition.starter_dashboard.grow_petition.copy_link.vanity_link_cta" />
				</Button>
			</form>
		</Box>
	);
};

export const CreateVanityUrlFormTitle: React.FC = () => {
	return (
		<Heading as="h1" size="h3" id="vanity-modal-title" mt="48px">
			<Translate value="corgi.petition.starter_dashboard.grow_petition.copy_link.vanity_link_title" />
		</Heading>
	);
};
